import type { AccessToken, IDToken, OktaAuth, RefreshToken, Tokens } from '@okta/okta-auth-js';

/**
 * Safari-only SSO bootstrap.
 *
 * Safari's ITP blocks the cross-site Okta session cookie (the Okta org lives on
 * `*.cargill.com`, the apps on `*.cglcloud.in`), so the standard silent-auth
 * flow (`session.get()` + `getWithoutPrompt()`) can never succeed — it always
 * reports `INACTIVE`.
 *
 * The DXP / MyCargill platform stores its Okta tokens as a single JSON object
 * in `localStorage` (previously three first-party cookies). We adopt them to
 * establish the session without any cross-site request.
 *
 * NOTE: unlike cookies, `localStorage` is scoped per-origin, not shared across
 * subdomains. This bootstrap only finds a session when this app and the DXP
 * platform run on the exact same origin.
 *
 * These tokens are minted for the parent (MyCargill) client, so their `cid`
 * differs from this app's `clientId`. The onboarding backend already accepts
 * them (the Account Management widget calls the API with this same token), and
 * `transform-auth-state` skips the client-id equality check while the marker
 * below is set.
 */
const DXP_TOKEN_STORAGE_KEY = 'dxp-token-storage';

/**
 * Per-tab marker signalling that the active Okta tokens were adopted from the
 * DXP shared localStorage entry. Consumed by `transform-auth-state`.
 */
export const DXP_SSO_MARKER = 'rc-dxp-sso';

/**
 * True when running in Safari (desktop or iOS), excluding Chrome/Edge/Firefox/
 * Opera builds that also include "Safari" in their user-agent string.
 */
export const isSafari = (): boolean => {
  if (typeof navigator === 'undefined') {
    return false;
  }

  const ua = navigator.userAgent;

  return /^((?!chrome|chromium|crios|android|edg|edgios|fxios|opr|opera).)*safari/i.test(ua);
};

interface DxpTokenStorage {
  accessToken?: AccessToken;
  idToken?: IDToken;
  refreshToken?: RefreshToken;
}

const readDxpTokenStorage = (): DxpTokenStorage | null => {
  const raw = window.localStorage.getItem(DXP_TOKEN_STORAGE_KEY);

  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as DxpTokenStorage;
  } catch {
    return null;
  }
};

/** Remove the DXP SSO marker (used on logout). */
export const clearDxpSsoMarker = (): void => {
  try {
    sessionStorage.removeItem(DXP_SSO_MARKER);
  } catch {
    // sessionStorage unavailable - nothing to clear.
  }
};

/**
 * Seed the Okta token manager from the DXP shared localStorage entry.
 *
 * @returns `true` when both an access token and an id token were found and
 * stored (so the caller can treat the user as authenticated); `false` when no
 * shared session is available and the caller should fall back to the normal
 * Okta sign-in flow.
 */
export const authenticateWithDxpSso = (oktaAuth: OktaAuth): boolean => {
  const dxpTokens = readDxpTokenStorage();
  const accessToken = dxpTokens?.accessToken ?? null;
  const idToken = dxpTokens?.idToken ?? null;
  const refreshToken = dxpTokens?.refreshToken ?? null;

  // okta-react only reports `isAuthenticated` when both tokens are present.
  if (!accessToken?.accessToken || !idToken?.idToken) {
    return false;
  }

  const tokens: Tokens = { accessToken, idToken };

  if (refreshToken?.refreshToken) {
    tokens.refreshToken = refreshToken;
  }

  oktaAuth.tokenManager.setTokens(tokens);

  try {
    sessionStorage.setItem(DXP_SSO_MARKER, '1');
  } catch {
    // sessionStorage unavailable - the cid check in transform-auth-state may
    // then reject a parent-minted token, but token storage still succeeded.
  }

  return true;
};

export default authenticateWithDxpSso;
